export type RealtimeConversationActivityKind = 'user' | 'agent';

export type RealtimeLeaseRenewal = {
  serverNow: number;
  leaseExpiresAt: number;
  leaseDurationMs: number;
};

type ActivityReporterArgs = {
  heartbeat: (
    activityKind: RealtimeConversationActivityKind
  ) => Promise<RealtimeLeaseRenewal>;
  isCurrentCall: () => boolean;
  onRenewed: (leaseDurationMs: number) => void;
  onError?: (error: unknown) => void;
  monotonicNow?: () => number;
  throttleMs?: number;
};

const DEFAULT_HEARTBEAT_THROTTLE_MS = 30_000;

export function createRealtimeLeaseActivityReporter(
  args: ActivityReporterArgs
): {
  report: (activityKind: RealtimeConversationActivityKind) => void;
} {
  const monotonicNow =
    args.monotonicNow ?? (() => globalThis.performance.now());
  const throttleMs = args.throttleMs ?? DEFAULT_HEARTBEAT_THROTTLE_MS;
  let lastHeartbeatAt: number | null = null;
  let latestRequestId = 0;

  return {
    report(activityKind) {
      if (!args.isCurrentCall()) return;
      const now = monotonicNow();
      if (lastHeartbeatAt !== null && now - lastHeartbeatAt < throttleMs) {
        return;
      }
      lastHeartbeatAt = now;
      const requestStartedAt = now;
      const requestId = ++latestRequestId;
      void args
        .heartbeat(activityKind)
        .then((renewal) => {
          if (
            !args.isCurrentCall() ||
            requestId !== latestRequestId ||
            !Number.isFinite(renewal.leaseDurationMs) ||
            renewal.leaseDurationMs < 0
          ) {
            return;
          }
          args.onRenewed(
            getRealtimeLeaseRemainingDurationMs({
              leaseDurationMs: renewal.leaseDurationMs,
              requestStartedAt,
              now: monotonicNow(),
            })
          );
        })
        .catch((error: unknown) => {
          if (args.isCurrentCall() && requestId === latestRequestId) {
            args.onError?.(error);
          }
        });
    },
  };
}

export function getRealtimeLeaseRemainingDurationMs(args: {
  leaseDurationMs: number;
  requestStartedAt: number;
  now: number;
}): number {
  const elapsedMs = Math.max(0, args.now - args.requestStartedAt);
  return Math.max(0, args.leaseDurationMs - elapsedMs);
}

export function shouldReportRealtimeUserActivityImmediately(args: {
  acceptedSpeech: boolean;
  pttActive: boolean;
}): boolean {
  return args.acceptedSpeech && args.pttActive;
}

export function getRealtimeTransportConversationActivity(args: {
  eventType: string;
  responseTicketKind?: string;
}): RealtimeConversationActivityKind | null {
  if (
    args.eventType === 'response.created' &&
    args.responseTicketKind === 'user_speech'
  ) {
    return 'user';
  }
  if (
    args.eventType === 'response.done' ||
    args.eventType === 'output_audio_buffer.started'
  ) {
    return 'agent';
  }
  return null;
}

export function reportRealtimeTransportConversationActivity(args: {
  eventType: string;
  responseTicketKind?: string;
  reportActivity: (activityKind: RealtimeConversationActivityKind) => void;
}): void {
  const activityKind = getRealtimeTransportConversationActivity(args);
  if (activityKind) args.reportActivity(activityKind);
}
